import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ClientCarousel from '../components/ClientCarousel';
import { ArrowRight, Shield, Network, Headphones, Zap } from 'lucide-react';

/* ==========================================
   Hero Slider Images & Content
========================================== */

const heroSlides = [
  {
    image:
      'https://images.unsplash.com/photo-1644088379091-d574269d422f?auto=format&fit=crop&w=1920&q=85',
    title: 'Powering Businesses with Smarter, Secure Connectivity',
    description:
      'Reliable network infrastructure and connectivity solutions designed to keep your business connected, secure, and ready for growth.',
  },

  {
    image:
      'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1920&q=85',
    title: 'Technology That Keeps Your Business Moving',
    description:
      'Modern technology solutions built to improve performance, reliability, and digital connectivity across your organization.',
  },

  {
    image:
      'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1920&q=85',
    title: 'Building Reliable Network Infrastructure',
    description:
      'From enterprise networking to structured infrastructure, we build dependable foundations for your digital operations.',
  },

  {
    image:
      'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=1920&q=85',
    title: 'Security Built Into Your Network',
    description:
      'Protect your business with secure network architecture, firewall solutions, and advanced security technologies.',
  },

  {
    image:
      'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1920&q=85',
    title: 'Cloud Connectivity for a Connected Future',
    description:
      'Connect your people, applications, and infrastructure with scalable solutions designed for the modern digital enterprise.',
  },

  {
    image:
      'https://images.unsplash.com/photo-1683322499436-f4383dd59f5a?auto=format&fit=crop&w=1920&q=85',
    title: 'Smarter Connectivity. Stronger Business.',
    description:
      'End-to-end networking and digital infrastructure solutions that help businesses operate with confidence.',
  },
];


const Home = () => {

  const [currentSlide, setCurrentSlide] = useState(0);


  /* ==========================================
     Automatic Hero Slider
  ========================================== */

  useEffect(() => {

    const interval = setInterval(() => {

      setCurrentSlide((previousSlide) =>
        (previousSlide + 1) % heroSlides.length
      );

    }, 5000);


    return () => clearInterval(interval);

  }, []);


  return (
    <div className="min-h-screen">


      {/* ==========================================
          Hero Section
      ========================================== */}

      <section className="relative h-[600px] md:h-[620px] flex items-center justify-center text-white overflow-hidden">


        {/* ==========================================
            Background Slides
        ========================================== */}

        {heroSlides.map((slide, index) => (

          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide
                ? 'opacity-100'
                : 'opacity-0'
            }`}
          >

            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />


            {/* ==========================================
                Strong Black Overlay
            ========================================== */}

            <div className="absolute inset-0 bg-black/70"></div>


            {/* ==========================================
                Additional Subtle Gradient
                Helps Text Visibility
            ========================================== */}

            <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30"></div>

          </div>

        ))}


        {/* ==========================================
            Hero Content
        ========================================== */}

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">

            {heroSlides[currentSlide].title}

          </h1>


          <p className="text-lg sm:text-xl md:text-2xl mb-8 text-gray-200 max-w-4xl mx-auto leading-relaxed">

            {heroSlides[currentSlide].description}

          </p>


          <Link
            to="/our-product"
            className="inline-flex items-center bg-red-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-lg"
          >

            Explore Our Services

            <ArrowRight className="ml-2 h-5 w-5" />

          </Link>

        </div>


        {/* ==========================================
            Slider Indicators
        ========================================== */}

        <div className="absolute bottom-8 left-0 right-0 z-20 flex justify-center gap-3">

          {heroSlides.map((_, index) => (

            <button
              key={index}
              type="button"
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={
                index === currentSlide
                  ? 'true'
                  : 'false'
              }
              className={`h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'w-8 bg-white'
                  : 'w-3 bg-white/50 hover:bg-white/80'
              }`}
            />

          ))}

        </div>

      </section>


      {/* ==========================================
          Client Carousel
      ========================================== */}

      <ClientCarousel />


      {/* ==========================================
          Customer Testimonials
      ========================================== */}

      <section className="py-16 bg-gray-100 relative overflow-hidden">

        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1600&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></div>


        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">

          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Customer Testimonials
          </h2>


          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">


            {/* Testimonial 1 */}

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">

              <div className="aspect-video">

                <iframe
                  src="https://player.vimeo.com/video/1102501735"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  title="Prasanna Kumar - Vindhya E Infomedia"
                ></iframe>

              </div>


              <div className="p-4">

                <h3 className="font-semibold text-lg">
                  Prasanna Kumar
                </h3>

                <p className="text-gray-600 text-sm">
                  Vindhya E Infomedia
                </p>

              </div>

            </div>


            {/* Testimonial 2 */}

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">

              <div className="aspect-video">

                <iframe
                  src="https://www.youtube.com/embed/6bXUjGe8c0s"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  title="Pramodh - India Sweet House"
                ></iframe>

              </div>


              <div className="p-4">

                <h3 className="font-semibold text-lg">
                  Pramodh
                </h3>

                <p className="text-gray-600 text-sm">
                  India Sweet House
                </p>

              </div>

            </div>


            {/* Testimonial 3 */}

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">

              <div className="aspect-video">

                <iframe
                  src="https://www.youtube.com/embed/R6Rh7mQPZnE"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  title="Ravi - InvenDis"
                ></iframe>

              </div>


              <div className="p-4">

                <h3 className="font-semibold text-lg">
                  Ravi
                </h3>

                <p className="text-gray-600 text-sm">
                  InvenDis
                </p>

              </div>

            </div>


            {/* Testimonial 4 */}

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">

              <div className="aspect-video">

                <iframe
                  src="https://www.youtube.com/embed/II_v3fU-2Yo"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  title="Sandeep & ShivGanesh - Hunger Box"
                ></iframe>

              </div>


              <div className="p-4">

                <h3 className="font-semibold text-lg">
                  Sandeep & ShivGanesh
                </h3>

                <p className="text-gray-600 text-sm">
                  Hunger Box
                </p>

              </div>

            </div>


            {/* Testimonial 5 */}

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">

              <div className="aspect-video">

                <iframe
                  src="https://www.youtube.com/embed/PekMhsKh6Aw"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  title="Chandrashekhar - Portea"
                ></iframe>

              </div>


              <div className="p-4">

                <h3 className="font-semibold text-lg">
                  Chandrashekhar
                </h3>

                <p className="text-gray-600 text-sm">
                  Portea
                </p>

              </div>

            </div>


            {/* Testimonial 6 */}

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">

              <div className="aspect-video">

                <iframe
                  src="https://www.youtube.com/embed/w1s6yCW-IPc"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  title="Naveen Hiremath - The Acoustic Saga"
                ></iframe>

              </div>


              <div className="p-4">

                <h3 className="font-semibold text-lg">
                  Naveen Hiremath
                </h3>

                <p className="text-gray-600 text-sm">
                  The Acoustic Saga
                </p>

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* ==========================================
          About IndyaNet
      ========================================== */}

      <section className="py-16 bg-gray-100 relative overflow-hidden">

        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=1600&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></div>


        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">

          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            About IndyaNet
          </h2>


          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">

              <h3 className="text-xl font-semibold mb-3 text-red-600">
                Our Vision
              </h3>

              <p className="text-gray-700">
                Our vision is to be the leading provider of telecommunications services in the region, delivering
                innovative solutions that empower our customers to connect and collaborate.
              </p>

            </div>


            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">

              <h3 className="text-xl font-semibold mb-3 text-red-600">
                Our Values
              </h3>

              <p className="text-gray-700">
                At IndyaNet, we are committed to operating with integrity, delivering quality services, and building
                lasting relationships with our customers.
              </p>

            </div>


            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">

              <h3 className="text-xl font-semibold mb-3 text-red-600">
                Our Expertise
              </h3>

              <p className="text-gray-700">
                Our team of experts has decades of combined experience in the telecommunications industry, and we are
                dedicated to staying ahead of the curve in terms of technology and innovation.
              </p>

            </div>

          </div>

        </div>

      </section>


      {/* ==========================================
          Why Choose IndyaNet
      ========================================== */}

      <section className="py-16 bg-white relative overflow-hidden">

        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1600&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></div>


        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">

          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Why Choose IndyaNet?
          </h2>


          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">

            <div className="bg-gray-50 p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 text-center">

              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">

                <Zap className="h-8 w-8 text-red-600" />

              </div>

              <h3 className="text-lg font-semibold mb-2">
                Enterprise-Grade Performance
              </h3>

              <p className="text-gray-600 text-sm">
                High-speed, reliable connectivity for mission-critical operations
              </p>

            </div>


            <div className="bg-gray-50 p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 text-center">

              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">

                <Shield className="h-8 w-8 text-red-600" />

              </div>

              <h3 className="text-lg font-semibold mb-2">
                Advanced Security
              </h3>

              <p className="text-gray-600 text-sm">
                Robust, cutting-edge security protocols
              </p>

            </div>


            <div className="bg-gray-50 p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 text-center">

              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">

                <Network className="h-8 w-8 text-red-600" />

              </div>

              <h3 className="text-lg font-semibold mb-2">
                Tailored Network Solutions
              </h3>

              <p className="text-gray-600 text-sm">
                Customized solutions for your unique business needs
              </p>

            </div>


            <div className="bg-gray-50 p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 text-center">

              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">

                <Headphones className="h-8 w-8 text-red-600" />

              </div>

              <h3 className="text-lg font-semibold mb-2">
                Dedicated Technical Assistance
              </h3>

              <p className="text-gray-600 text-sm">
                24/7 expert support whenever you need it
              </p>

            </div>

          </div>

        </div>

      </section>


      {/* ==========================================
          Our Offerings
      ========================================== */}

      <section className="py-16 bg-gray-100 relative overflow-hidden">

        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=1600&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></div>


        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">

          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Our Offerings
          </h2>


          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">

              <h3 className="text-xl font-semibold mb-3 text-red-600">
                Broadband Internet Solutions
              </h3>

              <p className="text-gray-700">
                High-speed internet connectivity tailored for business needs with better reliability and dedicated support.
              </p>

            </div>


            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">

              <h3 className="text-xl font-semibold mb-3 text-red-600">
                Private Leased Line Services
              </h3>

              <p className="text-gray-700">
                Dedicated, symmetric, and uncontended internet connections for mission-critical operations.
              </p>

            </div>


            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">

              <h3 className="text-xl font-semibold mb-3 text-red-600">
                SD-WAN & Enterprise Network Architecture
              </h3>

              <p className="text-gray-700">
                Software-defined networking solutions with dynamic path selection and centralized management.
              </p>

            </div>


            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">

              <h3 className="text-xl font-semibold mb-3 text-red-600">
                WhatsApp Business API & Messaging Solutions
              </h3>

              <p className="text-gray-700">
                Business messaging solutions to connect with customers seamlessly.
              </p>

            </div>

          </div>

        </div>

      </section>


      {/* ==========================================
          Our Mission
      ========================================== */}

      <section className="py-16 bg-white">

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

          <h2 className="text-3xl font-bold mb-6 text-gray-900">
            Our Mission
          </h2>


          <p className="text-lg text-gray-700 leading-relaxed">

            At IndyaNet, we are committed to empowering businesses with high-performance, secure, and scalable
            Enterprise Network Solutions tailored to meet the dynamic demands of today's digital landscape. In an era
            where connectivity drives innovation and productivity, we provide the technological backbone that enables
            seamless operations, enhanced collaboration, and sustainable growth.

          </p>


          <p className="text-lg text-gray-700 leading-relaxed mt-4">

            Our expertise lies in building robust and secure network infrastructures, offering a wide array of
            cutting-edge services including enterprise-grade broadband connectivity, WhatsApp Business solutions,
            private leased line services, software-defined WAN (SD-WAN), advanced firewall and threat management,
            cloud-based VPN and MPLS networks, VoIP and unified communication systems, and M2M (Machine-to-Machine)
            connectivity solutions. With IndyaNet, businesses gain a trusted partner dedicated to delivering
            future-ready connectivity and unmatched reliability.

          </p>

        </div>

      </section>


      {/* ==========================================
          Gallery Section
      ========================================== */}

      <section className="py-16 bg-gradient-to-b from-gray-100 to-gray-50 relative overflow-hidden">

        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1683322499436-f4383dd59f5a?w=1600&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></div>


        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">

            Explore our innovative telecom solutions gallery

          </h2>


          <div className="w-24 h-1 bg-red-600 mx-auto mb-12 rounded-full"></div>


          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

            {[
              {
                title: 'Enterprise Network Solution',
                description:
                  'High-speed internet service tailored for business needs, offering faster speeds, better reliability, and dedicated support.',
                image:
                  'https://images.unsplash.com/photo-1683322499436-f4383dd59f5a?crop=entropy&cs=srgb&fm=jpg&w=600&q=80',
              },

              {
                title: 'Internet Leased Line',
                description:
                  'Dedicated, symmetric, and uncontended internet connection for critical operations such as VoIP, cloud applications, and video conferencing.',
                image:
                  'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&w=600&q=80',
              },

              {
                title: 'Dual Internet Solution',
                description:
                  'Two separate internet connections providing redundancy, load balancing, and improved reliability for mission-critical operations.',
                image:
                  'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?crop=entropy&cs=srgb&fm=jpg&w=600&q=80',
              },

              {
                title: 'Firewall',
                description:
                  'Security system that monitors and controls network traffic based on predefined security rules to protect internal networks from threats.',
                image:
                  'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?crop=entropy&cs=srgb&fm=jpg&w=600&q=80',
              },

              {
                title: 'Call Center Solution',
                description:
                  'Scalable call center solutions with CRM integration, security features, and comprehensive support.',
                image:
                  'https://images.pexels.com/photos/8204317/pexels-photo-8204317.jpeg?auto=compress&cs=tinysrgb&w=600',
              },

              {
                title: 'SD-WAN',
                description:
                  'Centralized control, multiple link types, dynamic path selection, and application-aware routing for optimal performance.',
                image:
                  'https://images.pexels.com/photos/5087172/pexels-photo-5087172.jpeg?auto=compress&cs=tinysrgb&w=600',
              },

            ].map((item, index) => (

              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 group"
              >

                <div className="aspect-video overflow-hidden relative">

                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                </div>


                <div className="p-6">

                  <h3 className="text-xl font-bold mb-3 text-red-600 group-hover:text-red-700 transition-colors">

                    {item.title}

                  </h3>


                  <p className="text-gray-700 text-sm leading-relaxed">

                    {item.description}

                  </p>

                </div>

              </div>

            ))}

          </div>

        </div>

      </section>

    </div>
  );
};


export default Home;